import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import Label from '../Label';

RHFAutoComplete.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      exchange: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  multiple: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  getOptionLabel: PropTypes.func,
  isOptionEqualToValue: PropTypes.func,
  onChange: PropTypes.func,
};

export default function RHFAutoComplete({
  name,
  options,
  label,
  placeholder,
  multiple = true,
  getOptionLabel: customGetOptionLabel,
  isOptionEqualToValue: customIsOptionEqualToValue,
  onChange: customOnChange,
  ...other
}) {
  const { control } = useFormContext();

  const defaultGetOptionLabel = (option) => {
    if (typeof option === 'string') return option;
    return `${option.name} ${option.exchange?.name || ''}`;
  };

  const defaultIsOptionEqualToValue = (option, value) => {
    if (typeof value === 'string') {
      return option.name?.toString() === value || option === value;
    }
    return (
      option.name?.toString() === value.name?.toString() &&
      option.exchange?.name?.toString() === value.exchange?.name?.toString()
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          onChange={(event, newValue) => {
            if (customOnChange) {
              customOnChange(event, newValue);
            } else {
              field.onChange(newValue);
            }
          }}
          multiple={multiple}
          options={options}
          getOptionLabel={customGetOptionLabel || defaultGetOptionLabel}
          isOptionEqualToValue={customIsOptionEqualToValue || defaultIsOptionEqualToValue}
          disableCloseOnSelect
          value={field.value || []}
          renderOption={(props, option) => (
            <Box component="li" {...props} display="flex" justifyContent="space-between">
              <Typography sx={{ mx: 1 }} variant="body1">
                {typeof option === 'string' ? option : option.name}
              </Typography>
              {typeof option !== 'string' && option.exchange && (
                <Label variant="ghost" color="info">
                  {option.exchange.name}
                </Label>
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              error={!!error}
              helperText={error?.message}
              variant="outlined"
              label={label || 'Multiple Autocomplete'}
              placeholder={placeholder || 'Multiple Autocomplete'}
              {...other}
            />
          )}
        />
      )}
    />
  );
}
